import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetItemQuery } from '../store/docApi';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Chip,
  Grid,
  IconButton,
  Container,
  Link,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
// Format date and time using native JavaScript

export default function EventDetails() {
  const { pk, id } = useParams();
  const navigate = useNavigate();
  const { data: event, isLoading, error } = useGetItemQuery({ 
    label: 'event', 
    id: id 
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        Failed to load event: {error.message}
      </Typography>
    );
  }

  if (!event) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        Event not found
      </Typography>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/events/${pk}/${id}/edit`);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Events
        </Button>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          {event.imageUrl ? (
            <Box 
              component="img"
              src={event.imageUrl}
              alt={event.title}
              sx={{
                width: '100%',
                maxHeight: '400px',
                objectFit: 'cover',
                borderRadius: 1,
                mb: 3
              }}
            />
          ) : (
            <Box
              sx={{
                height: 200,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                borderRadius: 1
              }}
            >
              <EventIcon sx={{ fontSize: 80, color: 'grey.500' }} />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {event.title}
              </Typography>
              <Chip 
                label={event.category || 'General'} 
                color="primary" 
                size="small" 
                sx={{ textTransform: 'capitalize', mb: 2 }}
              />
            </Box>
            <IconButton 
              color="primary" 
              onClick={handleEdit}
              aria-label="Edit event"
            >
              <EditIcon />
            </IconButton>
          </Box>


          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon color="action" sx={{ mr: 1 }} />
                <Typography>
                  {formatDate(event.startDateTime)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimeIcon color="action" sx={{ mr: 1 }} />
                <Typography>
                  {formatTime(event.startDateTime)} - {formatTime(event.endDateTime)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <LocationIcon color="action" sx={{ mr: 1, mt: 0.5 }} />
                <Box>
                  {event.location?.type === 'online' ? (
                    <Link 
                      href={event.location.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <LinkIcon sx={{ mr: 0.5 }} />
                      Join Online Event
                    </Link>
                  ) : (
                    <Typography>
                      {event.location?.name && <Box fontWeight="bold">{event.location.name}</Box>}
                      {event.location?.address}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">Attendees</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {event.attendeeCount || 0} people are attending
                </Typography>
                
                <Button 
                  variant="contained" 
                  fullWidth 
                  sx={{ mb: 1 }}
                  onClick={() => {/* Handle registration */}}
                >
                  Register for Event
                </Button>
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => {
                    // Add to calendar functionality
                    const start = new Date(event.startDateTime).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/g, '');
                    const end = new Date(event.endDateTime).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/g, '');
                    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${start}/${end}&details=${encodeURIComponent(event.description || '')}&location=${encodeURIComponent(event.location?.address || event.location?.url || '')}`;
                    window.open(calendarUrl, '_blank');
                  }}
                >
                  Add to Calendar
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>About This Event</Typography>
            <Typography variant="body1" paragraph>
              {event.description || 'No description provided.'}
            </Typography>
          </Box>


          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={handleBack}
            >
              Back to Events
            </Button>
            <Button 
              variant="contained"
              onClick={() => {/* Handle registration */}}
            >
              Register Now
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
